import { env } from "@/shared/constants/env";
import { create } from "zustand";
import { isAxiosError } from "axios";
import {
  setAgentAvailability,
  createAgent,
  getAgentByUser,
  listAgents,
  type SettableAgentStatus,
} from "../services/agentService";
import { useSessionStore } from "../../../shared/stores/sessionStore";
import type { Agent, AgentStatus } from "../../../entities/agent";
import { toast } from "../../../shared/ui/Toast";
import { getErrorMessage } from "../../../shared/api/errors";

// Cache PERSISTANT de l'agentId, clé par userId. Survit au logout (clearSession
// vide sessionStore.agentId) et reste CORRECT par utilisateur (un agent B qui se
// connecte sur le device n'hérite pas de l'id de l'agent A). Évite de re-scanner
// GET /agents à chaque reconnexion.
const AGENT_ID_CACHE_PREFIX = "sosmain.agent_id:";

async function getCachedAgentId(userId: string): Promise<string | null> {
  try { return await localStorage.getItem(AGENT_ID_CACHE_PREFIX + userId); }
  catch { return null; }
}
async function cacheAgentId(userId: string, agentId: string): Promise<void> {
  try { await localStorage.setItem(AGENT_ID_CACHE_PREFIX + userId, agentId); }
  catch { /* best effort */ }
}

// Retrouve le profil agent par userId. Le backend N'A PAS de lookup par userId
// (GET /agents/{id} attend l'`id` de l'agent, ≠ userId ; le param ?userId= est
// ignoré) → on pagine GET /agents et on filtre côté client sur `userId`.
async function findAgentByUserId(userId: string): Promise<Agent | undefined> {
  const PER_PAGE = 100;
  for (let page = 1; page <= 20; page++) {
    const batch = await listAgents({ page, per_page: PER_PAGE });
    if (env.isDev) console.warn(`[agent] GET /agents (page ${page}, ${batch.length} agents) → réponse:`, JSON.stringify(batch));
    const mine = batch.find((a) => a.userId === userId);
    if (mine) {
      if (env.isDev) console.warn("[agent] agent trouvé par userId →", JSON.stringify(mine));
      return mine;
    }
    if (batch.length < PER_PAGE) return undefined; // dernière page atteinte
  }
  return undefined;
}

type AgentRuntimeState = {
  status:           AgentStatus;
  isToggling:       boolean;
  currentMissionId: string | null;

  // Bascule online/offline en passant par /agents/{id}/availability
  toggleOnline:       () => Promise<void>;
  /**
   * Force un statut de service.
   *
   * ⚠️ `BUSY` n'est PLUS posable par l'app (contrat du 2026-08-28) : le serveur
   * le pose lui-même à l'ÉMISSION d'une offre, et le poser à la main dérègle le
   * compteur de charge. `SUSPENDED` est un cul-de-sac dont rien ne sort. Seuls
   * `AVAILABLE` et `OFFLINE` restent du ressort de l'application.
   */
  setStatus:          (status: SettableAgentStatus) => Promise<void>;
  // Resynchronise le statut LOCAL depuis le profil backend (source de vérité,
  // AUCUN appel réseau). Le statut runtime repart à OFFLINE à chaque relaunch
  // de l'app : sans cette sync, un agent AVAILABLE/BUSY côté backend apparaissait
  // « hors ligne » et le tracker GPS restait muet (le client ne voyait pas
  // l'agent se déplacer). Ignorée pendant un toggle en cours (état transitoire).
  syncStatusFromProfile: (status: AgentStatus) => void;
  setCurrentMission:   (id: string) => void;
  clearCurrentMission: () => void;
  // Reset complet du runtime — appelé au logout pour éviter qu'un agent A
  // logout puis un agent B login voie le contexte (mission, status) de A.
  clearRuntime:        () => void;
};

export class AgentResolveError extends Error {
  constructor(
    message: string,
    public readonly cause?: unknown,
    public readonly recoverable: boolean = true,
  ) {
    super(message);
    this.name = "AgentResolveError";
  }
}

// Extrait le statut HTTP + le corps d'une erreur (axios) pour le diagnostic.
function axiosInfo(e: unknown): { status?: number; body?: unknown } {
  if (isAxiosError(e)) return { status: e.response?.status, body: e.response?.data };
  return {};
}

// Résout l'agentId courant. Cascade, du moins cher au plus cher :
// 1. Cache sessionStore.agentId (in-memory, vidé au logout)
// 2. Cache persistant localStorage par userId (survit au logout)
// 3. GET /agents/by-user/{sub} — le chemin NOMINAL depuis le 2026-08-28
// 4. POST /agents (création si premier login en tant qu'agent)
//    - 201 → on a un id (fraîchement créé)
//    - 401/403 → erreur d'auth/rôle, pas recoverable
//    - 409 (existe déjà) / autre → on retrouve l'agent par userId (étape 5)
// 5. GET /agents paginé, filtré par userId — dernier recours, coûteux.
async function resolveAgentId(): Promise<string> {
  const { agentId, userId, setAgentId } = useSessionStore.getState() as {
    agentId?:   string;
    userId?:    string;
    setAgentId: (id: string) => void;
  };
  if (agentId) return agentId;
  if (!userId) {
    throw new AgentResolveError("Aucune session active.", undefined, false);
  }

  // Cache persistant par userId — survit au logout (clearSession vide
  // sessionStore.agentId mais PAS cette clé) → reconnexion instantanée.
  const cached = await getCachedAgentId(userId);
  if (cached) {
    setAgentId(cached);
    return cached;
  }

  // Tentative 1 — la route DÉDIÉE. Une requête, contre un scan de vingt pages.
  //
  // `GET /agents/by-user/{sub}` est arrivée avec le contrat du 2026-08-28 : il
  // n'existe aucune route `/agents/me`, et `{agentId}` n'est pas le `sub` — le
  // passer dans `/agents/{id}` donne 404. C'est donc LE chemin nominal.
  //
  // Repli silencieux sur l'ancienne cascade en cas d'échec : la route est hors
  // du contrat embarqué, et l'espace agent ne doit pas mourir si elle manque.
  try {
    const mine = await getAgentByUser(userId);
    if (mine?.id) {
      setAgentId(mine.id);
      await cacheAgentId(userId, mine.id);
      return mine.id;
    }
  } catch (e) {
    if (env.isDev) {
      console.warn(
        "[agent] GET /agents/by-user a échoué — repli sur POST /agents puis scan:",
        axiosInfo(e).status ?? "(réseau)",
      );
    }
  }

  // Tentative 2 — POST /agents (création au premier login agent).
  let postError: unknown;
  try {
    const agent = await createAgent(userId);
    if (env.isDev) console.warn("[agent] POST /agents → réponse:", JSON.stringify(agent));
    if (agent.id) {
      setAgentId(agent.id);
      await cacheAgentId(userId, agent.id);
      return agent.id;
    }
    if (env.isDev) console.warn("[agent] POST /agents 2xx mais sans `id` — réponse:", agent);
    postError = new Error("POST /agents n'a pas retourné d'id");
  } catch (e: unknown) {
    postError = e;
    const { status, body } = axiosInfo(e);
    if (env.isDev) console.warn("[agent] POST /agents échec:", status ?? "(réseau)", body ?? (e as Error)?.message);
    if (status === 401 || status === 403) {
      throw new AgentResolveError(
        "Accès refusé au profil agent (vérifie ton rôle Keycloak).",
        e,
        false,
      );
    }
    // 409 (agent déjà existant) ou autre → on le retrouve par userId ci-dessous.
  }

  // Tentative 3 — dernier recours : l'agent EXISTE mais ni la route dédiée ni
  // la création n'ont abouti → on le retrouve en paginant GET /agents. Coûteux
  // (jusqu'à vingt pages de cent), d'où sa place en fin de cascade.
  try {
    const mine = await findAgentByUserId(userId);
    if (mine?.id) {
      setAgentId(mine.id);
      await cacheAgentId(userId, mine.id);
      return mine.id;
    }
    // POST a échoué ET l'agent est introuvable dans la liste → vrai problème.
    if (env.isDev) console.warn("[agent] agent introuvable dans GET /agents pour userId:", userId, "| POST:", axiosInfo(postError).status);
    throw new AgentResolveError(
      "Profil agent introuvable. Contacte le support.",
      postError,
      false,
    );
  } catch (e: unknown) {
    if (e instanceof AgentResolveError) throw e;
    const post = axiosInfo(postError);
    const list = axiosInfo(e);
    if (env.isDev) console.warn("[agent] listAgents échec:", list.status ?? "(réseau)", list.body);
    // On EXPOSE les statuts dans le message pour diagnostiquer sur le terrain.
    const recoverable = !isAxiosError(e) || (list.status ?? 500) >= 500;
    throw new AgentResolveError(
      `Impossible de récupérer le profil agent (POST ${post.status ?? "réseau"} · liste ${list.status ?? "réseau"}). Réessaie.`,
      e,
      recoverable,
    );
  }
}

// Résolution PROACTIVE de l'agentId au montage du stack agent (AgentStack).
// Sans ça, resolveAgentId n'était appelé qu'au premier toggleOnline/setStatus :
// un agent fraîchement connecté (ou après logout→login, clearSession vidant
// sessionStore.agentId) gardait agentId=undefined → useAgentActiveTrips et
// useAgentMissions restaient `disabled` → listes VIDES silencieuses, et le
// tracker GPS ne démarrait pas. Silencieux en cas d'échec (le toggle et les
// écrans retenteront) — on ne bloque pas le boot sur un souci réseau.
export async function ensureAgentIdResolved(): Promise<void> {
  try {
    await resolveAgentId();
  } catch (e) {
    if (env.isDev) {
      console.warn("[agent] résolution agentId au boot échouée (retentera au toggle):",
        e instanceof Error ? e.message : e);
    }
  }
}

export const useAgentStore = create<AgentRuntimeState>((set, get) => ({
  status:           "OFFLINE",
  isToggling:       false,
  currentMissionId: null,

  toggleOnline: async () => {
    if (get().isToggling) return;
    set({ isToggling: true });
    try {
      const next = get().status === "AVAILABLE" ? "OFFLINE" : "AVAILABLE";
      const agentId = await resolveAgentId();
      const agent = await setAgentAvailability(agentId, next);
      if (env.isDev) console.warn("[agent] POST /agents/{id}/availability → réponse:", JSON.stringify(agent));
      set({ status: agent.status ?? next });
    } catch (e) {
      // Feedback non bloquant : l'agent voit pourquoi l'action a échoué et
      // peut retry. Sans ce toast, on retombait silencieusement sur l'ancien
      // status — l'agent croyait être online alors qu'il ne l'était pas.
      const msg = e instanceof AgentResolveError ? e.message : getErrorMessage(e);
      toast.error(msg);
    } finally {
      set({ isToggling: false });
    }
  },

  setStatus: async (status) => {
    set({ isToggling: true });
    try {
      const agentId = await resolveAgentId();
      const agent = await setAgentAvailability(agentId, status);
      set({ status: agent.status ?? status });
    } catch (e) {
      const msg = e instanceof AgentResolveError ? e.message : getErrorMessage(e);
      toast.error(msg);
    } finally {
      set({ isToggling: false });
    }
  },

  syncStatusFromProfile: (status) => {
    if (get().isToggling)      return; // un toggle réseau est en cours, il fera foi
    if (get().status === status) return;
    set({ status });
  },

  setCurrentMission:   (id) => set({ currentMissionId: id }),
  clearCurrentMission: ()   => set({ currentMissionId: null }),

  clearRuntime: () =>
    set({ status: "OFFLINE", isToggling: false, currentMissionId: null }),
}));
